/**
 * Plan Resolution Engine
 * Priority: Manual Override (active, non-expired) → Paid Subscription
 *           → Sponsored Premium (?src=...) → Free default.
 */
import { supabase } from "@/integrations/supabase/client";
import { getSessionId } from "@/lib/session";
import { trackEvent } from "@/lib/audit";
import { detectSponsoredPlan } from "@/lib/ai/creditSystem";
import { RECOMMENDED_LIMITS, type PlanKey } from "@/lib/ai/tokenCostEstimator";

export type PlanCode = "free" | "pro" | "enterprise" | "sponsored_premium";
export type PlanSource =
  | "manual_override"
  | "paid_subscription"
  | "sponsored_premium"
  | "default_free";

export interface ResolvedPlan {
  plan_code: PlanCode;
  source: PlanSource;
  premium_enabled: boolean;
  ai_messages_per_day: number; // -1 = unlimited
  ai_token_limits: number; // -1 = unlimited
  advanced_predictions_enabled: boolean;
  historical_analogs_enabled: boolean;
  decisive_commentary_enabled: boolean;
  rewards_premium_enabled: boolean;
  ad_free_enabled: boolean;
  enterprise_features_enabled: boolean;
  expires_at?: string | null;
  notes?: string | null;
}

function planToLimitsKey(plan: PlanCode): PlanKey {
  if (plan === "enterprise") return "enterprise";
  if (plan === "pro" || plan === "sponsored_premium") return "pro";
  return "free";
}

export function buildPlan(plan: PlanCode, source: PlanSource, extras?: Partial<ResolvedPlan>): ResolvedPlan {
  const limits = RECOMMENDED_LIMITS[planToLimitsKey(plan)];
  const isPremium = plan !== "free";
  const isEnterprise = plan === "enterprise";
  return {
    plan_code: plan,
    source,
    premium_enabled: isPremium,
    ai_messages_per_day: limits.messagesPerDay,
    ai_token_limits: limits.tokensPerDay,
    advanced_predictions_enabled: isPremium,
    historical_analogs_enabled: isPremium,
    decisive_commentary_enabled: isPremium,
    rewards_premium_enabled: isPremium,
    ad_free_enabled: isEnterprise || plan === "sponsored_premium",
    enterprise_features_enabled: isEnterprise,
    expires_at: null,
    notes: null,
    ...extras,
  };
}

export interface ResolveOpts {
  email?: string;
  sessionId?: string;
  silent?: boolean;
}

/** Resolve the effective plan for the current session/email. */
export async function resolvePlan(opts: ResolveOpts = {}): Promise<ResolvedPlan> {
  const sessionId = opts.sessionId ?? getSessionId();
  const email = (opts.email ?? "").trim().toLowerCase();

  // 1. Manual override
  try {
    let q = supabase
      .from("billing_plan_overrides")
      .select("plan_code,status,expires_at,notes")
      .eq("status", "active")
      .order("created_at", { ascending: false })
      .limit(1);
    if (email) {
      q = q.eq("email", email);
    } else {
      q = q.eq("target_id", sessionId);
    }
    // P0-AUTH-08 — 4s timeout so Android Chrome `navigator.locks`
    // contention can never hang the Oracle / Insights bootstrap. On
    // timeout we fall through to the next resolution step (default free).
    const { data } = await Promise.race([
      q.maybeSingle(),
      new Promise<{ data: null }>((resolve) =>
        setTimeout(() => resolve({ data: null }), 4000),
      ),
    ]);
    if (data && (!data.expires_at || new Date(data.expires_at) > new Date())) {
      const plan = buildPlan(data.plan_code as PlanCode, "manual_override", {
        expires_at: data.expires_at,
        notes: data.notes,
      });
      if (!opts.silent) void trackEvent("plan_resolved", { source: plan.source, plan: plan.plan_code });
      return plan;
    }
  } catch {
    /* noop */
  }

  // 2. Paid subscription — placeholder hook for future Stripe/Paddle integration.

  // 3. Sponsored Premium via partner src
  const sponsored = detectSponsoredPlan();
  if (sponsored !== "free") {
    const plan = buildPlan("sponsored_premium", "sponsored_premium");
    if (!opts.silent) void trackEvent("plan_resolved", { source: plan.source, plan: plan.plan_code });
    return plan;
  }

  // 4. Default Free
  const plan = buildPlan("free", "default_free");
  if (!opts.silent) void trackEvent("plan_resolved", { source: plan.source, plan: plan.plan_code });
  return plan;
}
